import { Product, ProductInterface } from '../Model/Product';
import { AttributeUtility } from './AttributeUtility';

interface ArrayAccess<T = any> {
    [key: string]: T
}

export class ObjectUtility {
    public static unique<T>(collection: T[]): T[] {
        return Array.from(new Set(collection));
    }

    public static filter<T>(collection: (T | undefined | null)[]): T[] {
        return collection.filter(i => {
            return i !== undefined && i !== null;
        }) as T[];
    }

    public static valueForKeyPath<T extends object | ArrayAccess | undefined, R>(
        item: T,
        keyPath: string
    ): R | undefined {
        const parts = keyPath.split('.');

        return parts.reduce(
            (accumulator: any, pathPart, currentIndex) => {
                if (typeof accumulator === 'undefined') {
                    return undefined;
                }

                return ObjectUtility.valueForKeyInternal(accumulator, pathPart, parts[currentIndex - 1]);
            },
            item
        );
    }

    public static valueForKey<T, R>(item: T | ArrayAccess, key: string): R | undefined {
        return ObjectUtility.valueForKeyInternal(item, key, undefined);
    }

    private static valueForKeyInternal<T, R>(
        item: T | ArrayAccess,
        key: string,
        lastKey: string | undefined
    ): R | undefined {
        const arrayAccessValue = (item as ArrayAccess)[key];
        if (typeof arrayAccessValue !== 'undefined') {
            return arrayAccessValue;
        }
        if (key === '@parent') {
            return (item as ArrayAccess)['parent'];
        }
        if (key === '@child') {
            return (item as ArrayAccess)['children'];
        }

        if (item instanceof Product || (item as ArrayAccess)['attributes']) {
            return AttributeUtility.attributeForKey(item as ProductInterface, key) as any;
        }
        if (lastKey === '@child' && Array.isArray(item)) {
            return item.flatMap(subitem => ObjectUtility.valueForKeyInternal(subitem, key, key)) as any as R;
        } else {
            console.error(`Could not get value for key ${key} of item`, item);
            return undefined;
        }
    }
}
